import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarDays, Save, Droplets, Waves, Plus, MapPin, KeyRound } from "lucide-react";
import { toast } from "sonner";
import WaterCalculatorPopover from "@/components/WaterCalculatorPopover";
import { PhotoUploader } from "@/components/PhotoUploader";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import AddressFields from "@/components/AddressFields";
import PaymentMethodCard from "@/components/PaymentMethodCard";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Earliest date a customer is allowed to request — skips today AND the next
// business day. Example: Tue → Thu; Thu → Mon (skips Fri+weekend); Fri → Tue.
// Weekends never counted as business days.
const earliestRequestISO = () => {
  const d = new Date();
  let added = 0;
  while (added < 2) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d.toISOString().slice(0, 10);
};

/**
 * Self-service block for the customer portal:
 *  - Edit delivery schedule (weekly / biweekly / on call)
 *  - Request a chlorine-only delivery (status=pending until admin approves)
 */
export default function PortalSelfService({ onChanged }) {
  const [me, setMe] = useState(null);
  const [schedule, setSchedule] = useState("on_call");
  const [saving, setSaving] = useState(false);

  // Add pool dialog
  const blankPool = { name: "", address: "", city: "", state: "", zip_code: "", tank_size: "", water_gallons: "", entry_instructions: "", entry_photo_data_url: "" };
  const [openPool, setOpenPool] = useState(false);
  const [poolDraft, setPoolDraft] = useState(blankPool);
  const [savingPool, setSavingPool] = useState(false);
  const setP = (k, v) => setPoolDraft((p) => ({ ...p, [k]: v }));
  const setPMany = (patch) => setPoolDraft((p) => ({ ...p, ...patch }));

  // Inline entry (gate combination + photo) editor per existing pool
  const [entryEditId, setEntryEditId] = useState(null);
  const [entryForm, setEntryForm] = useState({ entry_photo_data_url: "", entry_instructions: "" });
  const [savingEntry, setSavingEntry] = useState(false);
  const openEntryEdit = (p) => {
    setEntryEditId(p.id);
    setEntryForm({
      entry_photo_data_url: p.entry_photo_data_url || "",
      entry_instructions: p.entry_instructions || "",
    });
  };
  const saveEntry = async (p) => {
    setSavingEntry(true);
    try {
      await api.patch(`/portal/pools/${p.id}/entry`, {
        entry_photo_data_url: entryForm.entry_photo_data_url || "",
        entry_instructions: entryForm.entry_instructions || "",
      });
      toast.success("Entry info saved");
      setEntryEditId(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSavingEntry(false); }
  };

  // Request dialog
  const [openReq, setOpenReq] = useState(false);
  const [poolId, setPoolId] = useState("");
  const [reqDate, setReqDate] = useState(earliestRequestISO());
  const [reqGallons, setReqGallons] = useState(0);
  const [reqNotes, setReqNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    return api.get("/portal/me")
      .then((r) => {
        const c = r.data.customer || {};
        const pools = r.data.pools || [];
        setMe(r.data);
        setSchedule(c.schedule || "on_call");
        if (pools.length === 1) {
          setPoolId(pools[0].id);
          setReqGallons(Number(pools[0].tank_size || 0));
        } else {
          setPoolId("");
          setReqGallons(0);
        }
      })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSchedule = async () => {
    setSaving(true);
    try {
      // Customer chooses cadence only; the specific weekday is set by the
      // office on the admin side so route planning stays under their control.
      await api.patch("/portal/me/schedule", { schedule });
      toast.success("Schedule updated");
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      await api.post("/portal/tickets/request", {
        pool_id: poolId || null,
        requested_date: reqDate,
        gallons: Number(reqGallons || 0) || null,
        notes: reqNotes,
      });
      toast.success("Chlorine request submitted — pending admin approval");
      setOpenReq(false);
      setReqNotes("");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const submitPool = async () => {
    if (!poolDraft.name.trim() || !poolDraft.address.trim()) {
      return toast.error("Pool name and address are required");
    }
    setSavingPool(true);
    try {
      await api.post("/portal/pools", {
        name: poolDraft.name.trim(),
        address: poolDraft.address.trim(),
        city: poolDraft.city || "",
        state: poolDraft.state || "",
        zip_code: poolDraft.zip_code || "",
        tank_size: Number(poolDraft.tank_size || 0),
        water_gallons: Number(poolDraft.water_gallons || 0),
        entry_instructions: poolDraft.entry_instructions || "",
        entry_photo_data_url: poolDraft.entry_photo_data_url || "",
      });
      toast.success("Pool added to your account");
      setOpenPool(false);
      setPoolDraft(blankPool);
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSavingPool(false); }
  };

  if (!me) return null;
  const pools = me.pools || [];
  const locked = !!(me.customer || {}).delivery_locked;
  const lockReason = (me.customer || {}).delivery_lock_reason || "";

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Payment method (card on file + auto-charge toggle) — full-width so customers see it first */}
      <section className="border border-border md:col-span-2" data-testid="portal-payment-method-section">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-display text-xl font-bold tracking-tight">Payment method</h2>
        </div>
        <div className="p-6">
          <PaymentMethodCard
            customer={{
              id: (me.customer || {}).id,
              card_on_file: (me.customer || {}).card_on_file,
              auto_charge_on_close: (me.customer || {}).auto_charge_on_close,
            }}
            onChange={() => load()}
            testIdPrefix="portal-pm"
            lockAutoCharge
          />
        </div>
      </section>

      {/* My Schedule */}
      <section className="border border-border" data-testid="portal-schedule-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">My delivery schedule</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred</Label>
            <Select value={schedule} onValueChange={setSchedule}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="portal-schedule-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Bi-weekly (every 2 weeks)</SelectItem>
                <SelectItem value="on_call">On call (I&apos;ll request each time)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">Our office will confirm the delivery day with you.</p>
          </div>
          <Button onClick={saveSchedule} disabled={saving} className="rounded-sm w-full" data-testid="portal-save-schedule">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
        </div>
      </section>

      {/* Request chlorine */}
      <section className="border border-border" data-testid="portal-request-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">Request a chlorine delivery</h2>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Need a one-off delivery between scheduled visits? Send a request and we&apos;ll confirm a date.
          </p>
          <Dialog open={openReq} onOpenChange={setOpenReq}>
            <DialogTrigger asChild>
              <Button className="rounded-sm w-full" data-testid="portal-open-request">
                <Droplets className="w-4 h-4 mr-2" /> Request chlorine delivery
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg rounded-sm">
              <DialogHeader>
                <DialogTitle>Request a chlorine delivery</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {locked && (
                  <p className="text-sm text-destructive font-medium" data-testid="portal-req-locked-msg">
                    {lockReason === "no_card"
                      ? "Delivery requests are locked until a payment card is added to your account (see Payment methods below)."
                      : "Delivery requests are locked due to outstanding unpaid invoices. Please pay your open invoices or call the office."}
                  </p>
                )}
                {pools.length === 0 && (
                  <p className="text-sm text-destructive">No pools on file. Add your pool first from the &quot;My pools&quot; section, then request a delivery.</p>
                )}
                {pools.length > 1 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Which pool</Label>
                    <Select value={poolId || "_all"} onValueChange={(v) => setPoolId(v === "_all" ? "" : v)}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="portal-req-pool"><SelectValue placeholder="All pools" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All pools</SelectItem>
                        {pools.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.address}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred date</Label>
                  <Input type="date" min={earliestRequestISO()} value={reqDate} onChange={(e) => setReqDate(e.target.value)} className="rounded-sm mt-1" data-testid="portal-req-date" />
                  <p className="text-[10px] text-muted-foreground mt-1">Earliest available date is 2 business days from today. For sooner please call the office.</p>
                </div>
                {pools.length <= 1 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Chlorine gallons (optional)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      className="rounded-sm mono-num mt-1"
                      value={reqGallons || ""}
                      onChange={(e) => setReqGallons(parseFloat(e.target.value || 0))}
                      data-testid="portal-req-gallons"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                  <Textarea value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} className="rounded-sm mt-1" rows={3} placeholder="e.g. preferred morning, gate code changed, etc." data-testid="portal-req-notes" />
                </div>
                <p className="text-[10px] text-muted-foreground font-mono">
                  Chlorine only — for other products please contact us directly.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenReq(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submitRequest} disabled={submitting || pools.length === 0 || locked} className="rounded-sm" data-testid="portal-submit-request">
                  {submitting ? "Submitting…" : "Submit request"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </section>
      {/* My pools */}
      <section className="border border-border md:col-span-2" data-testid="portal-pools-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Waves className="w-4 h-4 text-primary" />
            <h2 className="font-display text-xl font-bold tracking-tight">My pools</h2>
          </div>
          <Dialog open={openPool} onOpenChange={setOpenPool}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-sm" data-testid="portal-add-pool-button">
                <Plus className="w-4 h-4 mr-1" /> Add a pool
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg rounded-sm">
              <DialogHeader>
                <DialogTitle>Add a pool</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pool name *</Label>
                  <Input value={poolDraft.name} onChange={(e) => setP("name", e.target.value)}
                         className="rounded-sm mt-1" placeholder="e.g. Main pool, Backyard" data-testid="portal-pool-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Street address *</Label>
                  <AddressFields
                    value={{
                      address: poolDraft.address,
                      city: poolDraft.city,
                      state: poolDraft.state,
                      zip_code: poolDraft.zip_code,
                    }}
                    onChange={(patch) => setPMany(patch)}
                    showLabels={false}
                    testIdPrefix="portal-pool-address"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Chlorine tank size (gal)</Label>
                    <Input type="number" step="0.1" value={poolDraft.tank_size}
                           onChange={(e) => setP("tank_size", e.target.value)}
                           className="rounded-sm mono-num mt-1" data-testid="portal-pool-tank" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pool water (gal)</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Input type="number" step="1" value={poolDraft.water_gallons}
                             onChange={(e) => setP("water_gallons", e.target.value)}
                             className="rounded-sm mono-num" data-testid="portal-pool-water" />
                      <WaterCalculatorPopover onApply={(g) => setP("water_gallons", g)} testId="portal-pool-water-calc" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Combination / gate code / instructions (optional)</Label>
                  <Textarea value={poolDraft.entry_instructions} onChange={(e) => setP("entry_instructions", e.target.value)}
                            className="rounded-sm mt-1" rows={2}
                            placeholder="Gate code, where the tank is, dogs in yard, etc." data-testid="portal-pool-entry" />
                </div>
                <PhotoUploader
                  value={poolDraft.entry_photo_data_url}
                  onChange={(v) => setP("entry_photo_data_url", v)}
                  label="Entry / gate photo (optional)"
                  testId="portal-pool-entry-photo"
                />
                <p className="text-[10px] text-muted-foreground font-mono">
                  Not sure about tank size? Leave it at 0 — we&apos;ll confirm it on the first delivery.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenPool(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submitPool} disabled={savingPool} className="rounded-sm" data-testid="portal-pool-submit">
                  {savingPool ? "Adding…" : "Add pool"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="p-6">
          {pools.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="portal-no-pools">
              No pools on file yet. Add your pool so we know where to deliver — then you can request chlorine right away.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {pools.map((p) => (
                <div key={p.id} className="border border-border p-4" data-testid={`portal-pool-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {p.address}
                      </div>
                    </div>
                    {p.entry_photo_data_url ? (
                      <LivePhotoSurface value={p.entry_photo_data_url} alt="entry" className="w-10 h-10 rounded-sm border border-border shrink-0" testId={`portal-pool-entry-thumb-${p.id}`} />
                    ) : null}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-2">
                    Tank: {Number(p.tank_size || 0)} gal{Number(p.water_gallons || 0) > 0 ? ` · Water: ${Number(p.water_gallons)} gal` : ""}
                  </div>
                  {p.entry_instructions ? (
                    <div className="text-xs font-mono text-muted-foreground mt-1 whitespace-pre-wrap" data-testid={`portal-pool-entry-text-${p.id}`}>
                      <KeyRound className="w-3 h-3 inline mr-1 text-primary" />{p.entry_instructions}
                    </div>
                  ) : null}
                  <Button size="sm" variant="ghost"
                          onClick={() => (entryEditId === p.id ? setEntryEditId(null) : openEntryEdit(p))}
                          className="rounded-sm text-primary hover:text-primary mt-2 px-2 h-7"
                          data-testid={`portal-pool-entry-edit-${p.id}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Entry
                  </Button>
                  {entryEditId === p.id && (
                    <div className="border-t border-border mt-2 pt-3 space-y-3" data-testid={`portal-pool-entry-form-${p.id}`}>
                      <PhotoUploader
                        value={entryForm.entry_photo_data_url}
                        onChange={(v) => setEntryForm((f) => ({ ...f, entry_photo_data_url: v }))}
                        label="Entry / gate photo"
                        testId={`portal-pool-entry-photo-${p.id}`}
                      />
                      <div>
                        <Label className="text-xs">Combination / gate code / instructions</Label>
                        <Textarea
                          className="rounded-sm font-mono text-sm mt-1"
                          placeholder="e.g. Gate code 1234#&#10;Lockbox by garage, combo 4-9-1"
                          rows={3}
                          value={entryForm.entry_instructions}
                          onChange={(e) => setEntryForm((f) => ({ ...f, entry_instructions: e.target.value }))}
                          data-testid={`portal-pool-entry-input-${p.id}`}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEntryEditId(null)} className="rounded-sm">Cancel</Button>
                        <Button size="sm" onClick={() => saveEntry(p)} disabled={savingEntry} className="rounded-sm" data-testid={`portal-pool-entry-save-${p.id}`}>
                          {savingEntry ? "Saving…" : "Save entry info"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
